import {
  ElementType,
  ReactElement,
  RefObject,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Box, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { SingleValue } from "react-select";

import { Select } from "components/Select";
import { useBackToTop } from "contexts/BackToTopProvider";
import RequestParamsProvider from "contexts/RequestParamsProvider";
import useQueryParam, { QUERY_STRING_KEYS } from "hooks/useQueryParam";
import { detailsBodyPadding } from "util/styleHelpers";
import { DesktopOnly, MobileOnly } from "./BreakpointVisibility";
import { Filter } from "./Filters";
import TabHeading from "./TabHeading";

export type DetailTab<T extends string> = {
  tabName: T;
  component: ElementType;
  tabHeading: string | JSX.Element;
  filters: Filter[];
};

type ActiveIndicators = Record<string, boolean>;

const DetailsTabs = ({
  tabs,
  initialActiveIndicators = {},
  setErrorMessage,
}: {
  tabs: DetailTab<string>[];
  initialActiveIndicators?: ActiveIndicators;
  setErrorMessage(msg: string | undefined): void;
}): ReactElement => {
  const [selectedTabIndex, setSelectedTabIndex] = useQueryParam(
    QUERY_STRING_KEYS.SELECTED_TAB_KEY
  );

  const tabIndex = selectedTabIndex ? parseInt(selectedTabIndex) : 0;

  const handleTabsChange = (index: number) => {
    setSelectedTabIndex(index);
  };

  const { ref: scrollContainerRef, scrollToTop } = useBackToTop();

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, tabIndex]);

  const [indicators, setIndicators] = useState<ActiveIndicators>(
    initialActiveIndicators
  );

  const tabPanels = useMemo(
    () =>
      tabs.map(({ component: Component, tabName, filters }) => (
        <TabPanel p={0} key={tabName}>
          <RequestParamsProvider>
            <Component
              toggleIndicator={(active: boolean) =>
                setIndicators((currentIndicators) => ({
                  ...currentIndicators,
                  [tabName]: active,
                }))
              }
              setErrorMessage={setErrorMessage}
              filters={filters}
            />
          </RequestParamsProvider>
        </TabPanel>
      )),
    [tabs, setErrorMessage]
  );

  const tabSelectOptions = useMemo(
    () => tabs.map(({ tabName }, index) => ({ value: index, label: tabName })),
    [tabs]
  );

  const tabList = useMemo(
    () =>
      tabs.map(({ tabHeading, tabName }) => (
        <TabHeading key={tabName} indicator={indicators[tabName]}>
          {tabHeading}
        </TabHeading>
      )),
    [tabs, indicators]
  );

  return (
    <Tabs
      isFitted
      flex={1}
      index={tabIndex}
      onChange={handleTabsChange}
      isManual={
        true /* There appears to be a bug in onChange and react-router prompt. This makes the navigation prevention work */
      }
      overflow="hidden"
      colorScheme="black"
      display="flex"
      flexDirection="column"
      isLazy={false}
    >
      <MobileOnly>
        <Box p={detailsBodyPadding}>
          <Select
            value={tabSelectOptions[tabIndex]}
            options={tabSelectOptions}
            isClearable={false}
            isSearchable={false}
            onChange={
              ((option: SingleValue<SelectOption>) => {
                handleTabsChange(parseInt(option?.value ?? "0", 10));
              }) as any
            }
          />
        </Box>
      </MobileOnly>
      <DesktopOnly>
        <TabList>{tabList}</TabList>
      </DesktopOnly>
      <TabPanels
        overflowY="scroll"
        flex={1}
        ref={scrollContainerRef as RefObject<HTMLDivElement>}
        pb={10}
      >
        {tabPanels}
      </TabPanels>
    </Tabs>
  );
};

export default DetailsTabs;
